import { useTranslation } from 'react-i18next';
import { ACTION_KINDS, isReadAction, type ActionKind, type PermissionLevel } from '@pa/core';
import { limitLine } from '../../lib/permission-limit';
import type { PermissionView, PermissionsResponse } from '../../lib/api';
import { Check } from './icons';
import { SegmentedControl } from './SegmentedControl';

const MAIL_ACTIONS: readonly ActionKind[] = ['mail_read', 'mail_draft', 'mail_send'];

/**
 * You → What TWO can do (M4.5, M5.2). Each action class is a card; the level is the one
 * segmented control (neutral, the same at every width); reads are one "Can see" row without a
 * selector ("Reads always run."); the limit line sits only under Auto. Levels change only here.
 */
export function PermissionMatrix({
  view,
  offered,
  onChange,
}: {
  view: PermissionView;
  offered: PermissionsResponse['offered'];
  onChange: (action: ActionKind, level: PermissionLevel) => void;
}) {
  const { t } = useTranslation();
  const mailOff = view.mail_available === false;
  return (
    <ul className="mt-3 flex flex-col gap-3">
      {ACTION_KINDS.map((action) => {
        const p = view.permissions.find((x) => x.action === action) ?? {
          action,
          level: 'observe' as PermissionLevel,
        };
        const levels = offered[action] ?? ['observe'];
        const readOnly = isReadAction(action);
        const greyed = mailOff && MAIL_ACTIONS.includes(action);
        return (
          <li
            key={action}
            aria-disabled={greyed || undefined}
            className={[
              'rounded-lg border border-line bg-canvas p-3',
              greyed ? 'opacity-50' : '',
            ].join(' ')}
          >
            <p className="text-sm font-medium text-ink">{t(`permissions.actions.${action}`)}</p>
            {greyed ? (
              <p className="mt-1 text-xs text-ink-2" dir="auto">
                {t('connections.mailUnavailable')}
              </p>
            ) : readOnly ? (
              <p className="mt-2 flex items-center gap-2 text-sm text-ink">
                <Check size={14} className="text-success" strokeWidth={2.5} />
                {t('autonomy.short.observe')}
                <span className="text-xs text-ink-3">· {t('permissions.readsNote')}</span>
              </p>
            ) : (
              <>
                <div className="mt-2">
                  <SegmentedControl<PermissionLevel>
                    label={t(`permissions.actions.${action}`)}
                    value={p.level}
                    size="sm"
                    onChange={(level) => {
                      if (level !== p.level) onChange(action, level);
                    }}
                    options={levels.map((level) => ({
                      value: level,
                      label: t(`autonomy.short.${level}`),
                    }))}
                  />
                </div>
                {action === 'calendar_write' && p.level === 'auto' ? (
                  <p className="mt-2 text-xs text-ink-2" dir="auto">
                    {limitLine(p, t)}
                  </p>
                ) : action === 'mail_send' ? (
                  <p className="mt-2 text-xs text-ink-3">{t('permissions.sendNote')}</p>
                ) : null}
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}
